/**
 * REK-1986 (SSO.5): Clerk custom-flow transport for the Lemonade auth UI.
 *
 * Framework-free (no React imports) functions over the clerk-js client
 * resources (`clerk.client.signIn` / `clerk.client.signUp`), registered by
 * ClerkAuthProvider through the same registry seam as the token getter
 * (src/lib/clerk-token.ts). The Lemonade components never talk to Clerk
 * directly — useAuth()/useFastAuth() route here when
 * VITE_AUTH_PROVIDER=clerk, and to the legacy `multi-email-auth` edge
 * function otherwise.
 *
 * Flows implemented (spec: docs/context/FEATURE_SPECS/sso-clerk.md §5.4):
 * - Email OTP sign-in: signIn.create({ identifier }) → prepareFirstFactor
 *   (email_code, factor matched to the typed address — multi-email is
 *   native) → attemptFirstFactor → setActive.
 * - Phone OTP sign-in (NEW): same shape with phone_code.
 * - Email-code sign-up: signUp.create({ emailAddress, firstName, lastName })
 *   → prepareEmailAddressVerification → attemptEmailAddressVerification →
 *   setActive. Re-sends reuse the in-progress sign-up (prepare only — no
 *   CAPTCHA round trip).
 * - Social OAuth sign-in-or-up (REK-2137 Google; REK-2186 provider-agnostic):
 *   `signIn.authenticateWithRedirect({ strategy: 'oauth_<provider>' })` from
 *   shared SocialAuthButtons, completed on /auth/sso-callback via
 *   `completeSocialSsoCallback` — the Clerk Core 2 custom-flow callback
 *   contract: a `complete` sign-in activates directly; an unknown account
 *   surfaces as `signIn.firstFactorVerification.status === 'transferable'`
 *   and is TRANSFERRED to a sign-up with `signUp.create({ transfer: true })`
 *   (verified against clerk-js v5 + the legacy OAuth custom-flow docs).
 *
 * Error posture: Clerk API errors are ALWAYS mapped, never surfaced raw
 * (spec §8 — `form_identifier_not_found` becomes a flow status the caller
 * turns into the existing switch-to-signup / no-account copy; `user_banned`
 * becomes the existing account-locked copy).
 */
import { ACCOUNT_LOCKED_MESSAGE, SESSION_VERIFY_FAILED_MESSAGE } from '@/providers/auth-context';
import { supabase } from '@/integrations/supabase/client';
import { analytics } from '@/lib/analytics';
import { EXPERIMENTS } from '@/lib/analytics/experiments';
import { pushCompleteRegistration } from '@/lib/analytics/gtm';
import {
  readStoredAttribution,
  resolveEntrySource,
  type ReklaimAttribution,
} from '@/lib/analytics/properties';
import { grantImpliedConsent } from '@/lib/cookieConsent';
import { grantConsent } from '@/lib/recordConsent';
import {
  setLastSignInMethod,
  waitForClerkSessionResolution,
  getClerkAuthUser,
  clerkSignOut,
} from '@/lib/clerk-token';
import { getDetectedCountry } from '@/lib/geo';
import { safeDebug, safeError } from '@/lib/safeLog';

/** Active experiment assignments for W1-0 funnel props (mirrors pivot-signup). */
function getExperimentVariantsMap(): Record<string, string> {
  const variants: Record<string, string> = {};
  const cookieVariant = analytics.getFeatureFlag(EXPERIMENTS.COOKIE_CONSENT_VARIANT);
  if (typeof cookieVariant === 'string' && cookieVariant.length > 0) {
    variants[EXPERIMENTS.COOKIE_CONSENT_VARIANT] = cookieVariant;
  }
  return variants;
}

/**
 * Map `reklaim_attribution` → `profiles.signup_*` columns (REK-2146).
 * Inlined from pivot-signup to avoid a circular import (pivot-signup → this file).
 */
function profileUpdatesFromStoredAttribution(
  attribution: ReklaimAttribution,
): Record<string, string> {
  const updates: Record<string, string> = {};
  if (attribution.source) updates.signup_source = attribution.source;
  if (attribution.medium) updates.signup_medium = attribution.medium;
  if (attribution.campaign) updates.signup_campaign = attribution.campaign;
  if (attribution.fbclid) updates.signup_fbclid = attribution.fbclid;
  if (attribution.campaign_id) updates.signup_campaign_id = attribution.campaign_id;
  if (attribution.adset_id) updates.signup_adset_id = attribution.adset_id;
  if (attribution.ad_id) updates.signup_ad_id = attribution.ad_id;
  return updates;
}

/** sessionStorage key for the OAuth strategy across the full-page redirect. */
export const PENDING_SOCIAL_STRATEGY_KEY = 'reklaim_pending_social_strategy';

/** Inlined from pivot-signup.ts to avoid a circular dependency. */
const REFERRAL_CODE_PATTERN = /^[0-9A-F]{8}$/;
/** Inlined from pivot-signup.ts (same value). */
const PIVOT_RESOLUTION_TIMEOUT_MS = 100_000;

const OAUTH_STRATEGY_RE = /^oauth_[a-z0-9_]+$/i;

/** Normalize a Clerk social strategy to `oauth_<provider>` lowercase. */
export function normalizeOauthStrategy(strategy: string): `oauth_${string}` | null {
  const trimmed = strategy.trim().toLowerCase();
  if (!trimmed) return null;
  const withPrefix = trimmed.startsWith('oauth_') ? trimmed : `oauth_${trimmed}`;
  return OAUTH_STRATEGY_RE.test(withPrefix) ? (withPrefix as `oauth_${string}`) : null;
}

export function storePendingSocialStrategy(strategy: string): void {
  const normalized = normalizeOauthStrategy(strategy);
  if (!normalized) return;
  try {
    sessionStorage.setItem(PENDING_SOCIAL_STRATEGY_KEY, normalized);
  } catch (err) {
    safeError('SSO: failed to persist pending social strategy:', err);
  }
}

/**
 * Read AND clear the strategy stashed before `authenticateWithRedirect`.
 * Always clears so a stale strategy cannot leak into a later attempt.
 */
export function consumePendingSocialStrategy(): `oauth_${string}` | null {
  try {
    const stored = sessionStorage.getItem(PENDING_SOCIAL_STRATEGY_KEY);
    sessionStorage.removeItem(PENDING_SOCIAL_STRATEGY_KEY);
    return stored ? normalizeOauthStrategy(stored) : null;
  } catch (err) {
    safeError('SSO: failed to read pending social strategy:', err);
    return null;
  }
}

function resolveCompletedSocialMethod(
  fallback?: string | null,
): `oauth_${string}` {
  const pending = consumePendingSocialStrategy();
  if (pending) return pending;
  const fromFallback = fallback ? normalizeOauthStrategy(fallback) : null;
  return fromFallback ?? 'oauth_unknown';
}

/**
 * REK-2141: signup-time `unsafeMetadata` carried into the `clerk-webhook`.
 * Include the detected geo `country` (the `rkl_geo` cookie set by Vercel
 * middleware) when it is known, so the webhook writes `profiles.country`;
 * OMIT the key when unknown so the SERVER default (`'US'`) applies and a real
 * geo signal always wins over the fallback. Never client-default to `'US'`.
 * Clerk copies `unsafeMetadata` onto `User.unsafeMetadata` and delivers it as
 * `data.unsafe_metadata` on the `user.created` webhook.
 */
export function buildSignupUnsafeMetadata(): { country: string } | undefined {
  const country = getDetectedCountry();
  return country ? { country } : undefined;
}

// ── User-facing copy ───────────────────────────────────────────────────────

export const CLERK_AUTH_NOT_READY_MESSAGE =
  'Sign-in is still loading. Please try again in a moment.';
export const CLERK_GENERIC_AUTH_ERROR_MESSAGE =
  'Something went wrong. Please try again.';
export const CLERK_INCORRECT_CODE_MESSAGE =
  'That code is incorrect. Please check it and try again.';
export const CLERK_EXPIRED_CODE_MESSAGE =
  'That code has expired. Use “Resend” to get a fresh one, then try again.';
export const CLERK_TOO_MANY_ATTEMPTS_MESSAGE =
  'Too many incorrect attempts. Request a new code and try again.';
export const CLERK_CAPTCHA_FAILED_MESSAGE =
  'We could not verify that you are human. Please refresh the page and try again.';
export const CLERK_RATE_LIMITED_MESSAGE =
  'Too many requests. Please wait a moment and try again.';

// ── Structural clerk-js surface (keeps tests free of clerk-js classes) ─────

interface ClerkFlowFirstFactor {
  strategy: string;
  emailAddressId?: string;
  phoneNumberId?: string;
  safeIdentifier?: string;
  primary?: boolean;
}

/**
 * Structural slice of clerk-js's Verification resource — only what the
 * REK-2137 OAuth callback inspects (`status === 'transferable'` is the
 * Core 2 signal that the verified external account has no matching user /
 * an existing user, depending on which resource carries it).
 */
interface ClerkFlowVerification {
  status: string | null;
  error?: { code?: string } | null;
}

export interface ClerkFlowSignInResource {
  status: string | null;
  createdSessionId: string | null;
  supportedFirstFactors: ClerkFlowFirstFactor[] | null;
  firstFactorVerification?: ClerkFlowVerification | null;
  create(params: Record<string, unknown>): Promise<ClerkFlowSignInResource>;
  prepareFirstFactor(params: Record<string, unknown>): Promise<ClerkFlowSignInResource>;
  attemptFirstFactor(params: Record<string, unknown>): Promise<ClerkFlowSignInResource>;
  /**
   * Core 2 OAuth start: creates the sign-in attempt with the redirect
   * strategy and NAVIGATES the browser to the provider's consent screen.
   * Resolves only on failure paths (the success path unloads the page).
   */
  authenticateWithRedirect(params: Record<string, unknown>): Promise<void>;
}

export interface ClerkFlowSignUpResource {
  status: string | null;
  createdSessionId: string | null;
  emailAddress: string | null;
  verifications?: { externalAccount?: ClerkFlowVerification | null } | null;
  create(params: Record<string, unknown>): Promise<ClerkFlowSignUpResource>;
  prepareEmailAddressVerification(params?: Record<string, unknown>): Promise<ClerkFlowSignUpResource>;
  attemptEmailAddressVerification(params: Record<string, unknown>): Promise<ClerkFlowSignUpResource>;
}

export interface ClerkFlowInstance {
  client?: {
    signIn: ClerkFlowSignInResource;
    signUp: ClerkFlowSignUpResource;
  };
  /**
   * Structural slice of clerk-js's User resource (present once a session is
   * active). REK-2137 / REK-2186 read the OAuth-provided identity plus the
   * linked external accounts — the SSO discriminator must key on a VERIFIED
   * external account, NOT on name presence alone (the scan funnel's
   * `useFastAuth` also passes first/last name into `startEmailSignUp`, so
   * email-created users can carry Clerk names too — Bugbot R1).
   */
  user?: {
    firstName?: string | null;
    lastName?: string | null;
    imageUrl?: string | null;
    primaryEmailAddress?: { emailAddress?: string | null } | null;
    emailAddresses?: Array<{ emailAddress?: string | null } | null> | null;
    externalAccounts?: Array<{
      provider?: string | null;
      verification?: { status?: string | null } | null;
    } | null> | null;
    /**
     * clerk-js `UserResource.reload(): Promise<this>` (verified against the
     * installed @clerk types). Re-fetches the user resource — including the
     * freshly-linked external account's verification status — which the
     * `/auth/sso-callback` page needs because it renders on a fresh page load
     * where clerk-js may still hold the pre-link user snapshot (REK-2150).
     * Optional so structural test mocks can omit it.
     */
    reload?: () => Promise<unknown>;
  } | null;
  setActive(params: { session: string }): Promise<unknown>;
}

let flowInstance: ClerkFlowInstance | null = null;

/** Registered by ClerkAuthProvider as soon as clerk-js is available. */
export function registerClerkFlowInstance(instance: ClerkFlowInstance | null): void {
  flowInstance = instance;
}

export interface ClerkProvidedIdentity {
  firstName: string;
  lastName: string;
  email: string | null;
  imageUrl: string | null;
  /** Normalized provider slug without the `oauth_` prefix (e.g. `google`). */
  provider: string;
  /** Clerk strategy grammar: `oauth_<provider>`. */
  strategy: `oauth_${string}`;
}

/**
 * The signed-in user's SSO-provided identity (REK-2137 / REK-2186).
 *
 * Returns trimmed `firstName`/`lastName` (plus optional email/avatar) ONLY
 * when the active Clerk user has a VERIFIED external social account AND a
 * non-empty first name (providers mandate a given name; the family name is
 * optional, so `lastName` may legitimately be '' — mononym OK). Name presence
 * alone is NOT the discriminator: the scan funnel's `useFastAuth` passes typed
 * first/last names into `startEmailSignUp`, so email-created accounts can
 * carry Clerk names too (Bugbot R1).
 *
 * Callers use a non-null return to skip the /onboarding name+DOB gate
 * (name auto-persists, DOB deferred to the profile wizard) and to backfill
 * `profiles.full_name` / `avatar_url` for pre-fix social accounts.
 */
export function getClerkProvidedIdentity(): ClerkProvidedIdentity | null {
  const user = flowInstance?.user;
  if (!user) return null;
  const verifiedAccount = (user.externalAccounts ?? []).find((account) => {
    if (!account) return false;
    // clerk-js exposes `provider: 'google'`; the FAPI wire shape (and older
    // structural mocks) may carry the `oauth_` prefix — normalize both.
    const strategy = normalizeOauthStrategy(account.provider ?? '');
    return !!strategy && account.verification?.status === 'verified';
  });
  if (!verifiedAccount) return null;
  const strategy = normalizeOauthStrategy(verifiedAccount.provider ?? '');
  if (!strategy) return null;
  const firstName = (user.firstName ?? '').trim();
  const lastName = (user.lastName ?? '').trim();
  if (!firstName) return null;
  const email =
    (user.primaryEmailAddress?.emailAddress ?? '').trim() ||
    (user.emailAddresses ?? [])
      .map((e) => (e?.emailAddress ?? '').trim())
      .find((e) => e.length > 0) ||
    null;
  const imageUrl = (user.imageUrl ?? '').trim() || null;
  return {
    firstName,
    lastName,
    email,
    imageUrl,
    provider: strategy.replace(/^oauth_/, ''),
    strategy,
  };
}

/**
 * @deprecated Prefer `getClerkProvidedIdentity` — kept as a thin name-only
 * adapter for call sites that only need first/last during the REK-2186 cutover.
 */
export function getClerkProvidedName(): { firstName: string; lastName: string } | null {
  const identity = getClerkProvidedIdentity();
  if (!identity) return null;
  return { firstName: identity.firstName, lastName: identity.lastName };
}

// ── Persistence-time identity resolution (REK-2150) ────────────────────────

/**
 * Why `resolveClerkProvidedIdentityForPersist` returned what it did. Lets
 * persistence callers report an HONEST outcome instead of treating a null
 * result as either silent success or a hard error.
 *
 * - `resolved`        — a verified social identity is available to persist.
 * - `no-user`         — no active Clerk user (signed out / not yet hydrated).
 * - `pending-timeout` — a user exists but no VERIFIED social account with a
 *   first name appeared within the bound: either hydration never completed,
 *   or the session is genuinely non-social (an email/OTP user). Callers must
 *   NOT persist and must NOT claim success.
 * - `aborted`         — the active Clerk user cleared or changed mid-resolve
 *   (sign-out / account switch); never persist for the wrong account.
 */
export type PersistIdentityReason = 'resolved' | 'no-user' | 'pending-timeout' | 'aborted';

export interface ResolvedPersistIdentity {
  identity: ClerkProvidedIdentity | null;
  reason: PersistIdentityReason;
}

/** Poll cadence + default bound for `resolveClerkProvidedIdentityForPersist`. */
const PERSIST_IDENTITY_POLL_INTERVAL_MS = 200;
const PERSIST_IDENTITY_TIMEOUT_MS = 8_000;

/**
 * REK-2150: resolve the verified social identity for the PERSISTENCE path.
 *
 * The `/auth/sso-callback` page runs on a fresh full-page load straight off
 * the OAuth round trip. Right after `setActive`, clerk-js often still holds
 * the pre-link user snapshot, so `getClerkProvidedIdentity()` — which requires
 * a VERIFIED external account — returns null and the name write silently
 * no-ops (the Clerk session token independently carries the name, so the
 * greeting works while Core Identity stays empty). This resolver gives
 * hydration the time it needs: it reload()s the user resource once, then polls
 * `getClerkProvidedIdentity()` until it resolves or the bound elapses.
 *
 * It does NOT weaken the gate: the SAME verified-external-account + first-name
 * discriminator applies (an email-created Clerk user carrying typed names is
 * still rejected — it has no verified social account). The only thing that is
 * "looser" is timing, not trust. `getClerkProvidedIdentity()` is unchanged so
 * the onboarding gate that also consumes it keeps its exact semantics.
 */
export async function resolveClerkProvidedIdentityForPersist(
  options?: { timeoutMs?: number },
): Promise<ResolvedPersistIdentity> {
  // Fast path: already resolvable (returning sign-in with a hydrated user) —
  // no reload, no delay.
  const immediate = getClerkProvidedIdentity();
  if (immediate) return { identity: immediate, reason: 'resolved' };

  const initialUser = flowInstance?.user;
  if (!initialUser) return { identity: null, reason: 'no-user' };

  // Set the deadline BEFORE reload so the reload itself is bounded (the SSO
  // callback awaits this on both paths — a hung Clerk reload must not keep the
  // user on the spinner past the overall timeout; Bugbot R1).
  const deadline = Date.now() + (options?.timeoutMs ?? PERSIST_IDENTITY_TIMEOUT_MS);

  // Force a refetch of externalAccounts + names, RACED against the deadline.
  // Non-fatal: a reload failure (transient network) or a reload that outruns
  // the bound must not abort — we fall through to poll the current state,
  // which clerk-js may hydrate on its own, and the loop's deadline check
  // returns pending-timeout if it never does.
  if (typeof initialUser.reload === 'function') {
    try {
      await Promise.race([
        Promise.resolve(initialUser.reload()).catch((err) => {
          safeDebug('SSO persist identity reload failed:', err);
        }),
        new Promise((resolve) => setTimeout(resolve, Math.max(0, deadline - Date.now()))),
      ]);
    } catch (err) {
      safeDebug('SSO persist identity reload failed:', err);
    }
  }

  for (;;) {
    // Sign-out / account-switch guard: clerk-js `reload()` mutates in place and
    // returns the SAME resource, so a changed reference (or a cleared user)
    // means a different account is now active — never persist for it.
    const current = flowInstance?.user;
    if (!current || current !== initialUser) {
      return { identity: null, reason: 'aborted' };
    }

    const resolved = getClerkProvidedIdentity();
    if (resolved) return { identity: resolved, reason: 'resolved' };

    if (Date.now() >= deadline) return { identity: null, reason: 'pending-timeout' };
    await new Promise((resolve) => setTimeout(resolve, PERSIST_IDENTITY_POLL_INTERVAL_MS));
  }
}

/**
 * Poll cadence + bound for `waitForClerkFlowClient`. clerk-js loads within
 * ~1-2s of app mount; 15s covers slow devices/networks (same bound as
 * clerk-ticket.ts's redeemer wait).
 */
const FLOW_CLIENT_WAIT_INTERVAL_MS = 250;
const FLOW_CLIENT_WAIT_TIMEOUT_MS = 15_000;

/**
 * Wait (bounded) for a registered flow instance WITH a hydrated clerk-js
 * client. The /auth/sso-callback page runs on a fresh full-page load after
 * the OAuth round trip, so unlike the interactive flows above it cannot
 * assume clerk-js has finished loading — ClerkAuthProvider registers the
 * instance on mount, but `clerk.client` (and the signIn/signUp resources
 * carrying the OAuth verification outcome) only exists once the clerk-js
 * environment + client fetch completes. A plain bounded poll is the honest
 * mechanism: registration happens BEFORE hydration, so there is no
 * registration event to await for the client itself.
 */
async function waitForClerkFlowClient(
  timeoutMs: number = FLOW_CLIENT_WAIT_TIMEOUT_MS,
): Promise<ClerkFlowInstance | null> {
  const deadline = Date.now() + timeoutMs;
  for (;;) {
    const instance = flowInstance;
    if (instance?.client) return instance;
    if (Date.now() >= deadline) return null;
    await new Promise((resolve) => setTimeout(resolve, FLOW_CLIENT_WAIT_INTERVAL_MS));
  }
}

/** Test-only: reset registry state between test cases. */
export function __resetClerkFlowRegistryForTests(): void {
  flowInstance = null;
}

// ── Error mapping ──────────────────────────────────────────────────────────

export interface ClerkFlowError {
  /** First Clerk API error code, when the failure came from the Clerk API. */
  code: string | null;
  /** Mapped, user-facing message — never Clerk's raw copy for sensitive codes. */
  message: string;
  /** Clerk `user_banned` — maps to the existing account-locked copy. */
  accountLocked: boolean;
  rateLimited: boolean;
  retryAfter: number | null;
  /** `form_identifier_not_found` — the identifier has no Clerk account. */
  userNotFound: boolean;
  /** `form_identifier_exists` — sign-up hit an existing account. */
  userExists: boolean;
  /**
   * True for EXPECTED, user-recoverable flow outcomes (mistyped/expired OTP,
   * rate limit, existing/unknown identifier, banned/locked account, failed
   * CAPTCHA) — every one already surfaces mapped copy in the UI, so
   * `logClerkFlowFailure` logs them at debug level instead of error
   * (REK-2022 console hygiene). Set by `mapClerkFlowError` alongside the
   * code mapping so a new user-recoverable mapping can never forget it.
   */
  expected: boolean;
}

interface ClerkApiErrorLike {
  code?: string;
  message?: string;
  longMessage?: string;
}

function firstClerkApiError(err: unknown): ClerkApiErrorLike | null {
  const errors = (err as { errors?: unknown } | null)?.errors;
  if (Array.isArray(errors) && errors.length > 0 && errors[0] && typeof errors[0] === 'object') {
    return errors[0] as ClerkApiErrorLike;
  }
  return null;
}

function baseError(overrides: Partial<ClerkFlowError> & { message: string }): ClerkFlowError {
  return {
    code: null,
    accountLocked: false,
    rateLimited: false,
    retryAfter: null,
    userNotFound: false,
    userExists: false,
    expected: false,
    ...overrides,
  };
}

/**
 * Map any error thrown by a clerk-js flow call into a `ClerkFlowError`.
 * Exported for the D-9 email-capture modal, which runs its own Clerk user
 * calls but needs identical mapping semantics.
 */
export function mapClerkFlowError(err: unknown): ClerkFlowError {
  const apiError = firstClerkApiError(err);
  const status = (err as { status?: number } | null)?.status;
  const retryAfterRaw = (err as { retryAfter?: number } | null)?.retryAfter;
  const retryAfter =
    typeof retryAfterRaw === 'number' && Number.isFinite(retryAfterRaw) && retryAfterRaw > 0
      ? Math.ceil(retryAfterRaw)
      : null;

  if (apiError?.code) {
    const code = apiError.code;
    switch (code) {
      case 'form_identifier_not_found':
        return baseError({
          code,
          message: CLERK_GENERIC_AUTH_ERROR_MESSAGE,
          userNotFound: true,
          expected: true,
        });
      case 'form_identifier_exists':
      case 'identifier_already_signed_in':
        return baseError({
          code,
          message: CLERK_GENERIC_AUTH_ERROR_MESSAGE,
          userExists: true,
          expected: true,
        });
      case 'user_banned':
        return baseError({
          code,
          message: ACCOUNT_LOCKED_MESSAGE,
          accountLocked: true,
          expected: true,
        });
      case 'user_locked':
        // Clerk's TEMPORARY lockout (too many failed attempts) — distinct
        // from our permanent profiles.is_locked ban. Surface Clerk's own
        // "try again in N minutes" copy rather than the support copy.
        return baseError({
          code,
          message: apiError.longMessage || apiError.message || CLERK_TOO_MANY_ATTEMPTS_MESSAGE,
          expected: true,
        });
      case 'form_code_incorrect':
        return baseError({ code, message: CLERK_INCORRECT_CODE_MESSAGE, expected: true });
      case 'verification_expired':
        return baseError({ code, message: CLERK_EXPIRED_CODE_MESSAGE, expected: true });
      case 'verification_failed':
        return baseError({ code, message: CLERK_TOO_MANY_ATTEMPTS_MESSAGE, expected: true });
      case 'too_many_requests':
        return baseError({
          code,
          message: CLERK_RATE_LIMITED_MESSAGE,
          rateLimited: true,
          retryAfter,
          expected: true,
        });
      default:
        if (code.startsWith('captcha_')) {
          return baseError({ code, message: CLERK_CAPTCHA_FAILED_MESSAGE, expected: true });
        }
        return baseError({
          code,
          message: apiError.longMessage || apiError.message || CLERK_GENERIC_AUTH_ERROR_MESSAGE,
        });
    }
  }

  if (status === 429) {
    return baseError({
      code: null,
      message: CLERK_RATE_LIMITED_MESSAGE,
      rateLimited: true,
      retryAfter,
      expected: true,
    });
  }

  return baseError({ code: null, message: CLERK_GENERIC_AUTH_ERROR_MESSAGE });
}

/**
 * True when the mapped failure is an expected, user-recoverable flow outcome
 * (a mistyped/expired OTP, a resend hitting the rate limit, sign-up with an
 * already-registered identifier, a banned/locked account, a failed CAPTCHA).
 * Expectedness is assigned per-branch inside `mapClerkFlowError` — adding a
 * new user-recoverable mapping marks it there, so this can never drift out
 * of sync with the code list.
 */
export function isExpectedClerkFlowError(mapped: ClerkFlowError): boolean {
  return mapped.expected;
}

/**
 * REK-2022 console hygiene: expected user-recoverable failures (wrong OTP,
 * rate limit, existing account, …) log at debug level — visible with the
 * console's Verbose filter but silent by default, so a production console
 * never shows a red error for a user simply mistyping a code. Anything
 * unexpected (unknown Clerk codes, network failures) keeps the error-level
 * safeError path so genuine defects stay loud.
 */
export function logClerkFlowFailure(context: string, mapped: ClerkFlowError, raw: unknown): void {
  if (isExpectedClerkFlowError(mapped)) {
    // Never log the raw error object on the expected path — raw Clerk API /
    // network errors can carry request metadata. The code (or a static label
    // for the code-less bare-429 case) is all diagnosis needs.
    safeDebug(context, mapped.code ?? (mapped.rateLimited ? 'rate_limited' : 'expected_flow_error'));
  } else if (mapped.code) {
    // Unexpected but code-bearing (unknown Clerk API code): log the code AND
    // the raw error — status/longMessage/meta matter for diagnosing a code we
    // did not anticipate. safeError sanitizes both.
    safeError(context, mapped.code, raw);
  } else {
    safeError(context, raw);
  }
}

// ── Sign-in (email + phone OTP) ────────────────────────────────────────────

export type ClerkSignInStrategy = 'email_code' | 'phone_code';

export type StartSignInResult =
  | { status: 'sent'; verifyIdentifier: string }
  | { status: 'user_not_found' }
  | { status: 'already_signed_in_admin' }
  | { status: 'switch_account'; signedInAs: string | null }
  | { status: 'error'; error: ClerkFlowError };

function notReadyError(): ClerkFlowError {
  return baseError({ message: CLERK_AUTH_NOT_READY_MESSAGE });
}

interface PickedFirstFactor {
  factor: ClerkFlowFirstFactor;
  /** What the "sent" panel shows — never claims the typed address unless the factor matched it. */
  verifyIdentifier: string;
}

function pickFirstFactor(
  factors: ClerkFlowFirstFactor[] | null,
  strategy: ClerkSignInStrategy,
  identifier: string,
): PickedFirstFactor | null {
  const candidates = (factors ?? []).filter((f) => f.strategy === strategy);
  if (candidates.length === 0) return null;
  const normalized = identifier.trim().toLowerCase();
  const exact = candidates.find(
    (f) => f.safeIdentifier?.trim().toLowerCase() === normalized,
  );
  if (exact) {
    return { factor: exact, verifyIdentifier: exact.safeIdentifier || identifier };
  }
  // Defensive fallback: Clerk returns the full safeIdentifier for the factor
  // matching the typed identifier, so this branch means the identifiers came
  // back masked/unmatchable. Send to the primary (else first) factor and
  // display THAT factor's own (possibly masked) safeIdentifier — never tell
  // the user the code went to the address they typed when we could not match
  // it (Bugbot R1 on PR #601).
  const fallback = candidates.find((f) => f.primary) ?? candidates[0];
  return { factor: fallback, verifyIdentifier: fallback.safeIdentifier || identifier };
}

async function startCodeSignIn(
  strategy: ClerkSignInStrategy,
  identifier: string,
): Promise<StartSignInResult> {
  const client = flowInstance?.client;
  if (!client) return { status: 'error', error: notReadyError() };

  try {
    const signIn = await client.signIn.create({ identifier });
    const picked = pickFirstFactor(signIn.supportedFirstFactors, strategy, identifier);
    if (!picked) {
      // The account exists but cannot receive this factor (e.g. phone entry
      // for an email-only import). Mirror the no-account handling — the
      // caller renders the strategy-appropriate copy.
      return { status: 'user_not_found' };
    }
    await client.signIn.prepareFirstFactor(
      strategy === 'email_code'
        ? { strategy, emailAddressId: picked.factor.emailAddressId }
        : { strategy, phoneNumberId: picked.factor.phoneNumberId },
    );
    return { status: 'sent', verifyIdentifier: picked.verifyIdentifier };
  } catch (err) {
    const mapped = mapClerkFlowError(err);
    if (mapped.userNotFound) return { status: 'user_not_found' };
    logClerkFlowFailure('Clerk sign-in start failed:', mapped, err);
    return { status: 'error', error: mapped };
  }
}

/**
 * Start an email-code sign-in. Sends the 6-digit OTP to the typed address
 * (secondary emails resolve natively — every verified `user_emails` address
 * is imported as a Clerk email address, replacing the `multi-email-auth`
 * lookup).
 *
 * Default `signUpIfMissing: true` lets Clerk synthesize an email_code factor for
 * first-time addresses so the consumer OTP flow works without a prior sign-up
 * step. When the returned first-factor verification reaches `transferable` the
 * caller should complete the sign-up via `completeEmailSignUpTransfer`.
 *
 * Pass `{ signUpIfMissing: false }` on surfaces that must stay sign-in-only
 * (e.g. `/admin/login`) so unknown emails return `user_not_found` instead of
 * minting a signup OTP.
 */
export async function startEmailCodeSignIn(
  email: string,
  options?: { signUpIfMissing?: boolean },
): Promise<StartSignInResult> {
  const client = flowInstance?.client;
  if (!client) return { status: 'error', error: notReadyError() };

  const signUpIfMissing = options?.signUpIfMissing !== false;

  // Whether the pre-check below already cleared a stale session — the
  // session_exists catch must not sign out twice for one call.
  let sessionCleared = false;
  let signedInAs: string | null = null;

  try {
    // Admin-only path (signUpIfMissing: false, e.g. `/admin/login`):
    // single-session guard. A live session makes `signIn.create` throw
    // `session_exists`, so resolve it BEFORE creating: an admin session
    // short-circuits (no create, no sign-out), a non-admin session is signed
    // out first and the typed address proceeds. Consumer flows (default
    // `signUpIfMissing: true`) skip this entirely.
    if (!signUpIfMissing) {
      const existingUser = getClerkAuthUser();
      if (existingUser) {
        signedInAs =
          flowInstance?.user?.primaryEmailAddress?.emailAddress?.trim() ||
          existingUser.email?.trim() ||
          null;
        const { data, error } = await supabase.rpc('has_role', {
          _user_id: existingUser.id,
          _role: 'admin',
        });
        if (error || typeof data !== 'boolean') {
          safeError('Admin session role check failed:', error);
          return {
            status: 'error',
            error: baseError({ message: CLERK_GENERIC_AUTH_ERROR_MESSAGE }),
          };
        }
        if (data) {
          return { status: 'already_signed_in_admin' };
        }
        await clerkSignOut();
        sessionCleared = true;
      }
    }

    const signIn = await client.signIn.create(
      signUpIfMissing
        ? { identifier: email, signUpIfMissing: true }
        : { identifier: email },
    );
    const picked = pickFirstFactor(signIn.supportedFirstFactors, 'email_code', email);
    if (!picked) {
      return { status: 'user_not_found' };
    }
    await client.signIn.prepareFirstFactor({
      strategy: 'email_code',
      emailAddressId: picked.factor.emailAddressId,
    });
    return { status: 'sent', verifyIdentifier: picked.verifyIdentifier };
  } catch (err) {
    const mapped = mapClerkFlowError(err);
    // Admin / sign-in-only path: Clerk throws form_identifier_not_found when
    // signUpIfMissing is omitted and the address has no account. Mirror
    // startCodeSignIn so callers get the enumeration-neutral user_not_found
    // status (not a generic "try again" error).
    if (mapped.userNotFound) return { status: 'user_not_found' };
    // Admin-only path: a session that appeared after the pre-check (or was
    // never registered) makes `signIn.create` throw `session_exists`
    // (Clerk 400). Sign out once so the retry starts clean, and surface who
    // is signed in so the caller can render switch-account copy. Gated to
    // the admin path — consumer flows keep the generic mapping.
    if (!signUpIfMissing && firstClerkApiError(err)?.code === 'session_exists') {
      signedInAs =
        signedInAs ||
        flowInstance?.user?.primaryEmailAddress?.emailAddress?.trim() ||
        getClerkAuthUser()?.email?.trim() ||
        null;
      if (!sessionCleared) {
        await clerkSignOut();
      }
      safeDebug('Clerk email sign-in start: session_exists, signed out for account switch');
      return {
        status: 'switch_account',
        signedInAs,
      };
    }
    logClerkFlowFailure('Clerk email sign-in start failed:', mapped, err);
    return { status: 'error', error: mapped };
  }
}

/** Start a phone-code (SMS OTP) sign-in for an E.164 phone number. */
export function startPhoneCodeSignIn(phoneE164: string): Promise<StartSignInResult> {
  return startCodeSignIn('phone_code', phoneE164);
}

export type AttemptCodeResult =
  | { status: 'complete' }
  | { status: 'transferable'; safeIdentifier: string }
  | { status: 'error'; error: ClerkFlowError };

export interface AttemptCodeOptions {
  /**
   * Invoked after the code verifies but BEFORE the session activates —
   * the signup flow applies implied cookie consent here so PostHog is
   * opted-in before the provider's identify() fires (same ordering contract
   * as the legacy setSession path).
   */
  onBeforeSessionActivate?: () => void;
}

/**
 * Attempt the prepared first factor with the user's 6-digit code and, on
 * success, activate the created session (ClerkAuthProvider then resolves the
 * legacy identity and reports through `waitForClerkSessionResolution`).
 *
 * When `startEmailCodeSignIn` was called with `signUpIfMissing: true` and the
 * address belongs to a new user, clerk-js@6 throws a `ClerkAPIError` with
 * `code === 'sign_up_if_missing_transfer'`. Some clerk-js variants instead
 * resolve with `firstFactorVerification.status === 'transferable'`. Both shapes
 * return `{ status: 'transferable', safeIdentifier }` so the caller can invoke
 * `completeEmailSignUpTransfer`.
 *
 * Prefer the caller-supplied `safeIdentifier` (originally submitted email);
 * Clerk may mask the one stored on the sign-in object.
 */
export async function attemptSignInCode(
  strategy: ClerkSignInStrategy,
  code: string,
  options?: AttemptCodeOptions & { safeIdentifier?: string },
): Promise<AttemptCodeResult> {
  const instance = flowInstance;
  const client = instance?.client;
  if (!instance || !client) return { status: 'error', error: notReadyError() };

  try {
    const result = await client.signIn.attemptFirstFactor({ strategy, code });

    // Defensive: some clerk-js variants resolve transferable without throwing.
    // clerk-js@6 email_code + signUpIfMissing normally throws instead (see catch).
    if (
      strategy === 'email_code' &&
      result.firstFactorVerification?.status === 'transferable'
    ) {
      const fromFactor =
        (result.supportedFirstFactors ?? []).find((f) => f.strategy === 'email_code')
          ?.safeIdentifier ?? '';
      return {
        status: 'transferable',
        safeIdentifier: options?.safeIdentifier || fromFactor,
      };
    }

    if (result.status !== 'complete' || !result.createdSessionId) {
      safeError('Clerk sign-in attempt did not complete:', result.status);
      return { status: 'error', error: baseError({ message: CLERK_GENERIC_AUTH_ERROR_MESSAGE }) };
    }
    options?.onBeforeSessionActivate?.();
    setLastSignInMethod(strategy === 'phone_code' ? 'phone' : 'email');
    await instance.setActive({ session: result.createdSessionId });
    return { status: 'complete' };
  } catch (err) {
    // New-user path (clerk-js@6 + signUpIfMissing: true): Clerk throws a
    // ClerkAPIError with code 'sign_up_if_missing_transfer' instead of
    // resolving with a transferable status. Return the transferable sentinel
    // so the caller can route to completeEmailSignUpTransfer.
    const apiError = firstClerkApiError(err);
    if (strategy === 'email_code' && apiError?.code === 'sign_up_if_missing_transfer') {
      return { status: 'transferable', safeIdentifier: options?.safeIdentifier ?? '' };
    }
    const mapped = mapClerkFlowError(err);
    logClerkFlowFailure('Clerk sign-in attempt failed:', mapped, err);
    return { status: 'error', error: mapped };
  }
}

// ── Sign-up (email code) ───────────────────────────────────────────────────

export type StartSignUpResult =
  | { status: 'sent' }
  | { status: 'user_exists' }
  | { status: 'error'; error: ClerkFlowError };

export interface StartEmailSignUpParams {
  email: string;
  firstName?: string;
  lastName?: string;
}

/**
 * Inlined from pivot-signup.ts (`TERMS_AFFIRMATION_REQUIRED_MESSAGE`) to avoid
 * a circular import (pivot-signup imports attemptSignUpEmailCode from here).
 */
export const EMAIL_TRANSFER_TERMS_REQUIRED_MESSAGE =
  'Please confirm you are 18 or older and agree to the Terms before creating your account.';

/**
 * Complete a sign-up transfer after `attemptSignInCode` returns
 * `{ status: 'transferable' }`.  This function mirrors the OAuth
 * `completeSocialSsoCallback` pattern for the email-code path.
 *
 * Inlines the session-tail logic from `pivot-signup.ts`
 * (`completePivotSignupSession` + `persistPivotSignupRecords`) to avoid a
 * circular import.
 *
 * ToS affirmation is mandatory for this API (new-user email transfer): callers
 * must pass `tosAccepted: true` and a non-empty `termsAcceptedAtIso`. Missing
 * either rejects before `signUp.create({ transfer: true })` so consent cannot
 * be silently skipped.
 */
export async function completeEmailSignUpTransfer(params: {
  tosAccepted: boolean;
  /** ISO timestamp from the in-box ToS checkbox — required, never invented. */
  termsAcceptedAtIso: string;
  referralCode?: string | null;
}): Promise<{ status: 'complete' } | { status: 'error'; error: ClerkFlowError }> {
  const instance = flowInstance;
  const client = instance?.client;
  if (!instance || !client) return { status: 'error', error: notReadyError() };

  const tosAccepted = params.tosAccepted === true;
  const termsAcceptedAtIso = params.termsAcceptedAtIso.trim();
  if (!tosAccepted || termsAcceptedAtIso.length === 0) {
    return {
      status: 'error',
      error: baseError({ message: EMAIL_TRANSFER_TERMS_REQUIRED_MESSAGE, expected: true }),
    };
  }
  const referralCode = params.referralCode ?? null;

  try {
    // Transfer the sign-in verification to a brand-new sign-up.
    // Identifier comes from the transferable sign-in session — no email arg needed.
    // REK-2141: carry detected geo like OAuth transfer / startEmailSignUp.
    const unsafeMetadata = buildSignupUnsafeMetadata();
    const signUp = await client.signUp.create({
      transfer: true,
      ...(unsafeMetadata ? { unsafeMetadata } : {}),
    });

    // Activate the newly created session.
    if (!signUp.createdSessionId) {
      safeError('Clerk sign-up transfer did not produce a session:', signUp.status);
      return { status: 'error', error: baseError({ message: CLERK_GENERIC_AUTH_ERROR_MESSAGE }) };
    }

    // Grant implied cookie consent before session activation (same ordering as
    // pivot-signup / useFastAuth — PostHog opt-in before identify).
    const didAutoConsent = grantImpliedConsent('otp_login');

    setLastSignInMethod('email');
    await instance.setActive({ session: signUp.createdSessionId });

    // Wait for the Supabase session to resolve via the Clerk token (mirrors
    // the completePivotSignupSession + persistPivotSignupRecords logic).
    const resolution = await waitForClerkSessionResolution(PIVOT_RESOLUTION_TIMEOUT_MS);
    if (resolution === 'denied-locked') {
      return { status: 'error', error: baseError({ message: ACCOUNT_LOCKED_MESSAGE, accountLocked: true }) };
    }
    if (resolution !== 'granted') {
      return { status: 'error', error: baseError({ message: SESSION_VERIFY_FAILED_MESSAGE }) };
    }

    if (didAutoConsent) {
      analytics.trackEvent({
        name: 'consent_decision',
        properties: {
          decision: 'analytics_only',
          variant: 'none',
          time_to_decision_ms: 0,
          trigger: 'signup_implied',
        },
      });
    }
    analytics.trackEvent({ name: 'email_verified', properties: {} });

    const attribution = readStoredAttribution();
    const userId = getClerkAuthUser()?.id;
    if (userId) {
      // Persist signup records — inlined from persistPivotSignupRecords.
      // ToS gate above guarantees tosAccepted + non-empty termsAcceptedAtIso.
      // REK-2146: also write UTM/Meta signup_* columns from reklaim_attribution
      // (parity with pivot-signup / FastAuth — this is the default /auth path).
      try {
        const { error } = await supabase
          .from('profiles')
          .update({
            terms_accepted_at: termsAcceptedAtIso,
            ...profileUpdatesFromStoredAttribution(attribution),
          })
          .eq('id', userId);
        if (error) {
          safeError('Email transfer signup: profiles attribution/terms update failed:', error.message);
        }
      } catch (err) {
        safeError('Email transfer signup: profiles attribution/terms update threw:', err);
      }

      for (const consentType of ['terms_of_service', 'age_verification'] as const) {
        try {
          await grantConsent(consentType);
        } catch (err) {
          safeError(`Email transfer signup: ${consentType} consent grant failed:`, err);
        }
      }

      // Attribution stores `ref` (see Auth.tsx) — not `referral_code`.
      // Inlined from readAttributionReferralCode to avoid circular import.
      let storedRef: string | null = null;
      try {
        const code =
          typeof attribution.ref === 'string' ? attribution.ref.trim().toUpperCase() : '';
        storedRef = REFERRAL_CODE_PATTERN.test(code) ? code : null;
      } catch {
        storedRef = null;
      }
      const callerRef =
        referralCode && REFERRAL_CODE_PATTERN.test(referralCode.trim().toUpperCase())
          ? referralCode.trim().toUpperCase()
          : null;
      const resolvedReferralCode = callerRef ?? storedRef;

      if (resolvedReferralCode) {
        try {
          const { data, error } = await supabase.functions.invoke('process-referral', {
            body: { referralCode: resolvedReferralCode },
          });
          if (error) {
            safeError('Email transfer signup: process-referral failed:', error);
          } else if (data?.success) {
            analytics.trackEvent({
              name: 'referral_signup_completed',
              properties: { referring_user_id: data.referrerId || 'unknown' },
            });
          }
        } catch (err) {
          safeError('Email transfer signup: process-referral threw:', err);
        }
      }
    } else {
      safeError('Email transfer signup: session granted but no resolved user id');
    }

    const entrySource = resolveEntrySource(attribution);
    const experimentVariants = getExperimentVariantsMap();
    analytics.trackEvent({
      name: 'signup_completed',
      properties: {
        origin: 'email',
        auth_method: 'email_otp',
        entry_source: entrySource,
        experiment_variants: experimentVariants,
      },
    });
    analytics.trackEvent({
      name: 'registration_completed',
      properties: { method: 'email' },
    });
    // App half of Meta CompleteRegistration via GTM (REK-2146). Consent-gated;
    // covers the default /auth one-box email signup transfer path.
    pushCompleteRegistration({
      auth_method: 'email_otp',
      entry_source: entrySource,
      experiment_variants: experimentVariants,
      signup_campaign: attribution.campaign,
      signup_campaign_id: attribution.campaign_id,
    });

    return { status: 'complete' };
  } catch (err) {
    const mapped = mapClerkFlowError(err);
    logClerkFlowFailure('Clerk email sign-up transfer failed:', mapped, err);
    return { status: 'error', error: mapped };
  }
}

/**
 * Create the Clerk sign-up and send the email verification code.
 *
 * Create-or-resend semantics: when the client already holds an in-progress
 * sign-up for the SAME email (status `missing_requirements`), only
 * `prepareEmailAddressVerification` runs — a resend mints a fresh code
 * without a second CAPTCHA round trip (the `<div id="clerk-captcha">` mount
 * only exists on the reveal step where the first create happens).
 *
 * `form_identifier_exists` maps to `user_exists` — the caller routes to the
 * login flow exactly like today's existing-account redirect.
 */
export async function startEmailSignUp(
  params: StartEmailSignUpParams,
): Promise<StartSignUpResult> {
  const client = flowInstance?.client;
  if (!client) return { status: 'error', error: notReadyError() };

  const email = params.email.trim();
  try {
    const inProgress =
      client.signUp.status === 'missing_requirements' &&
      client.signUp.emailAddress?.trim().toLowerCase() === email.toLowerCase();
    if (!inProgress) {
      const unsafeMetadata = buildSignupUnsafeMetadata();
      await client.signUp.create({
        emailAddress: email,
        ...(params.firstName ? { firstName: params.firstName } : {}),
        ...(params.lastName ? { lastName: params.lastName } : {}),
        ...(unsafeMetadata ? { unsafeMetadata } : {}),
      });
    }
    await client.signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
    return { status: 'sent' };
  } catch (err) {
    const mapped = mapClerkFlowError(err);
    if (mapped.userExists) return { status: 'user_exists' };
    logClerkFlowFailure('Clerk sign-up start failed:', mapped, err);
    return { status: 'error', error: mapped };
  }
}

/**
 * Attempt the sign-up email verification with the user's 6-digit code and,
 * on success, activate the created session. The SSO.2 clerk-webhook then
 * provisions the shadow auth.users + profiles rows; ClerkAuthProvider polls
 * for them and reports through `waitForClerkSessionResolution`.
 */
export async function attemptSignUpEmailCode(
  code: string,
  options?: AttemptCodeOptions,
): Promise<AttemptCodeResult> {
  const instance = flowInstance;
  const client = instance?.client;
  if (!instance || !client) return { status: 'error', error: notReadyError() };

  try {
    const result = await client.signUp.attemptEmailAddressVerification({ code });
    if (result.status !== 'complete' || !result.createdSessionId) {
      safeError('Clerk sign-up attempt did not complete:', result.status);
      return { status: 'error', error: baseError({ message: CLERK_GENERIC_AUTH_ERROR_MESSAGE }) };
    }
    options?.onBeforeSessionActivate?.();
    setLastSignInMethod('email');
    await instance.setActive({ session: result.createdSessionId });
    return { status: 'complete' };
  } catch (err) {
    const mapped = mapClerkFlowError(err);
    logClerkFlowFailure('Clerk sign-up attempt failed:', mapped, err);
    return { status: 'error', error: mapped };
  }
}

// ── Social OAuth sign-in-or-up (REK-2137 Google; REK-2186 provider-agnostic) ─

export interface StartSocialAuthParams {
  /**
   * Clerk OAuth strategy (`oauth_google`, `oauth_facebook`, …). Must already
   * be enabled + authenticatable on the Clerk instance.
   */
  strategy: string;
  /**
   * Where Clerk sends the browser back after the provider round trip —
   * the SPA's /auth/sso-callback route (relative paths resolve against the
   * current origin, so the same code serves beta./web./localhost). Clerk
   * validates it against the instance's registered domains; beta. and web.
   * are registered on their instances (verified 2026-08-03).
   */
  redirectUrl: string;
  /**
   * Clerk's `redirectUrlComplete` — only consumed by Clerk's OWN
   * handleRedirectCallback, which this app does not use (the callback page
   * completes the flow manually to satisfy the pivot-signup contract).
   * Passed anyway because the Core 2 param is required.
   */
  redirectUrlComplete: string;
}

export type StartSocialAuthResult =
  | { status: 'redirecting' }
  | { status: 'error'; error: ClerkFlowError };

/**
 * Start a social OAuth flow: creates the sign-in attempt with the given
 * `oauth_<provider>` redirect strategy and NAVIGATES the browser to the
 * provider's consent screen. On success this function technically resolves
 * while the page is unloading — callers should leave their pending UI in
 * place. Always started from `signIn` (never `signUp`): the callback
 * transfers to a sign-up when the provider returns an unknown account, which
 * keeps a stale in-progress email sign-up on the client from contaminating
 * the OAuth attempt.
 */
export async function startSocialAuthRedirect(
  params: StartSocialAuthParams,
): Promise<StartSocialAuthResult> {
  const client = flowInstance?.client;
  if (!client) return { status: 'error', error: notReadyError() };

  const strategy = normalizeOauthStrategy(params.strategy);
  if (!strategy) {
    return {
      status: 'error',
      error: baseError({ message: CLERK_GENERIC_AUTH_ERROR_MESSAGE }),
    };
  }

  storePendingSocialStrategy(strategy);

  try {
    await client.signIn.authenticateWithRedirect({
      strategy,
      redirectUrl: params.redirectUrl,
      redirectUrlComplete: params.redirectUrlComplete,
    });
    return { status: 'redirecting' };
  } catch (err) {
    consumePendingSocialStrategy();
    const mapped = mapClerkFlowError(err);
    logClerkFlowFailure('Clerk social OAuth start failed:', mapped, err);
    return { status: 'error', error: mapped };
  }
}

export type SocialSsoCallbackOutcome =
  /** Existing account — session activated; treat as a login. */
  | { status: 'signed-in'; method: `oauth_${string}` }
  /** Brand-new account created via the sign-up transfer — session activated. */
  | { status: 'signed-up'; method: `oauth_${string}` }
  /**
   * The outcome was a NEW account but the caller could not supply a real
   * 18+/ToS affirmation timestamp. NO account was created (the refusal runs
   * BEFORE `signUp.create`), so the user can simply retry from /auth.
   */
  | { status: 'terms-missing' }
  /** No pending OAuth attempt (direct visit / canceled at the provider). */
  | { status: 'no-attempt' }
  | { status: 'error'; error: ClerkFlowError };

export interface CompleteSocialSsoCallbackOptions {
  /**
   * Invoked when the callback resolves to a BRAND-NEW account, BEFORE the
   * transferred sign-up creates it. Must return the user's real affirmation
   * timestamp (captured on the pivot screen before the redirect) or null to
   * REFUSE account creation — pivot-signup.ts's write contract forbids
   * completing an account without one, and refusing before `signUp.create`
   * means no half-created account exists.
   */
  confirmSignUpTermsAcceptance: () => string | null;
  /**
   * Sign-up path only: runs after the transferred sign-up completes but
   * BEFORE the session activates — the pivot applies implied cookie consent
   * here so PostHog is opted-in before ClerkAuthProvider's identify() fires
   * (same ordering contract as attemptSignUpEmailCode).
   */
  onBeforeSignUpSessionActivate?: (method: `oauth_${string}`) => void;
}

/**
 * Complete the social OAuth round trip on /auth/sso-callback (Clerk Core 2
 * custom-flow contract, verified against clerk-js v5 + the legacy OAuth
 * docs):
 *
 * 1. `signIn.status === 'complete'` — the provider account matched an existing
 *    user; activate the session (a plain sign-in).
 * 2. `signIn.firstFactorVerification.status === 'transferable'` — no user
 *    for this provider account; TRANSFER to a sign-up
 *    (`signUp.create({ transfer: true })` — may run the smart CAPTCHA, so
 *    the callback page must mount `<div id="clerk-captcha">`), then
 *    activate. The SSO.2 webhook provisions the legacy shadow rows and the
 *    caller finishes the pivot-signup contract.
 * 3. `signUp.verifications.externalAccount.status === 'transferable'` —
 *    defensive reverse transfer (sign-up flow hit an existing account);
 *    not reachable from this app's sign-in-first start, but handled so a
 *    future signUp-initiated flow cannot strand the user.
 * 4. Neither — no pending attempt (direct visit, or the user canceled on
 *    the provider's screen).
 *
 * Waits (bounded) for clerk-js to hydrate first: this runs on a fresh page
 * load straight off the OAuth redirect. The completed `method` is the
 * stashed `oauth_<provider>` strategy (never a bare `'google'`).
 */
export async function completeSocialSsoCallback(
  options: CompleteSocialSsoCallbackOptions,
): Promise<SocialSsoCallbackOutcome> {
  const instance = await waitForClerkFlowClient();
  const client = instance?.client;
  if (!instance || !client) return { status: 'error', error: notReadyError() };

  // Resolve once up front so every success path records the same method and
  // the sessionStorage key is cleared even on early returns below.
  const method = resolveCompletedSocialMethod();

  try {
    const { signIn, signUp } = client;

    // 1. Existing account — the OAuth verification completed the sign-in.
    if (signIn.status === 'complete' && signIn.createdSessionId) {
      setLastSignInMethod(method);
      await instance.setActive({ session: signIn.createdSessionId });
      return { status: 'signed-in', method };
    }

    // 2. Brand-new account — transfer the verified external account to a
    //    sign-up. The terms affirmation is confirmed BEFORE the account is
    //    created so a refusal leaves nothing behind.
    if (signIn.firstFactorVerification?.status === 'transferable') {
      const termsAcceptedAtIso = options.confirmSignUpTermsAcceptance();
      if (!termsAcceptedAtIso) {
        return { status: 'terms-missing' };
      }
      // REK-2141: carry the detected geo through the OAuth transfer sign-up
      // too (the /auth/sso-callback page still has the rkl_geo cookie). When
      // unknown, omit it and let the webhook default to 'US'.
      const unsafeMetadata = buildSignupUnsafeMetadata();
      const created = await signUp.create({
        transfer: true,
        ...(unsafeMetadata ? { unsafeMetadata } : {}),
      });
      if (created.status !== 'complete' || !created.createdSessionId) {
        safeError('Clerk social sign-up transfer did not complete:', created.status);
        return { status: 'error', error: baseError({ message: CLERK_GENERIC_AUTH_ERROR_MESSAGE }) };
      }
      options.onBeforeSignUpSessionActivate?.(method);
      setLastSignInMethod(method);
      await instance.setActive({ session: created.createdSessionId });
      return { status: 'signed-up', method };
    }

    // 3. Defensive reverse transfer (signUp-initiated flow met an existing
    //    account). Kept for contract completeness per the Clerk callback
    //    docs; this app always starts from signIn.
    if (signUp.verifications?.externalAccount?.status === 'transferable') {
      const transferred = await signIn.create({ transfer: true });
      if (transferred.status === 'complete' && transferred.createdSessionId) {
        setLastSignInMethod(method);
        await instance.setActive({ session: transferred.createdSessionId });
        return { status: 'signed-in', method };
      }
      safeError('Clerk social sign-in transfer did not complete:', transferred.status);
      return { status: 'error', error: baseError({ message: CLERK_GENERIC_AUTH_ERROR_MESSAGE }) };
    }

    // 4. Defensive: a sign-up that completed WITHOUT a transfer (clerk-js's
    //    own handleRedirectCallback carries the same branch for
    //    signUp-initiated flows). The terms gate applies — this is still a
    //    brand-new account.
    if (signUp.status === 'complete' && signUp.createdSessionId) {
      const termsAcceptedAtIso = options.confirmSignUpTermsAcceptance();
      if (!termsAcceptedAtIso) {
        return { status: 'terms-missing' };
      }
      options.onBeforeSignUpSessionActivate?.(method);
      setLastSignInMethod(method);
      await instance.setActive({ session: signUp.createdSessionId });
      return { status: 'signed-up', method };
    }

    // 5. Nothing pending — direct visit or the user backed out at the provider.
    return { status: 'no-attempt' };
  } catch (err) {
    const mapped = mapClerkFlowError(err);
    logClerkFlowFailure('Clerk social SSO callback failed:', mapped, err);
    return { status: 'error', error: mapped };
  }
}
